/** Backend model allowlist; keep ids synchronized with src/components/chat/chatModels.ts. */
import type { Infer } from "convex/values";
import type { agentOverridesValidator } from "../lib/schema/scenarios";
import { SUBSCRIPTION_MODELS } from "./subscriptionModels";

type SupportedModel = {
	id: string;
	contextWindowTokens: number;
	priceTierStartsAtTokens?: number;
	compactAtTokens?: number;
	targetPromptTokens?: number;
};

export const SUPPORTED_MODELS: SupportedModel[] = [
	{
		id: "google/gemini-3.1-pro-preview",
		contextWindowTokens: 1_048_576,
		priceTierStartsAtTokens: 200_000,
	},
	{ id: "google/gemini-3.7-flash", contextWindowTokens: 1_048_576 },
	{ id: "google/gemini-3-flash-preview", contextWindowTokens: 1_048_576 },
	{ id: "google/gemini-3.5-flash-lite", contextWindowTokens: 1_048_576 },
	...SUBSCRIPTION_MODELS,
	{ id: "deepseek/deepseek-v4-pro", contextWindowTokens: 1_048_576 },
	{ id: "deepseek/deepseek-v4-flash-0731", contextWindowTokens: 1_048_576 },
	{ id: "deepseek/deepseek-v3.2-exp", contextWindowTokens: 163_840 },
	{ id: "moonshotai/kimi-k3", contextWindowTokens: 1_048_576 },
	{ id: "moonshotai/kimi-k2.6", contextWindowTokens: 262_144 },
	{ id: "qwen/qwen3-235b-a22b-2507", contextWindowTokens: 262_144 },
	{
		id: "x-ai/grok-4.6",
		contextWindowTokens: 500_000,
		priceTierStartsAtTokens: 200_000,
	},
	{
		id: "x-ai/grok-4.5",
		contextWindowTokens: 500_000,
		priceTierStartsAtTokens: 200_000,
	},
	{
		id: "x-ai/grok-4.3",
		contextWindowTokens: 1_000_000,
		priceTierStartsAtTokens: 200_000,
	},
	{ id: "minimax/minimax-m2.7", contextWindowTokens: 204_800 },
	{ id: "z-ai/glm-5.1", contextWindowTokens: 204_800 },
	{ id: "z-ai/glm-5.2", contextWindowTokens: 1_048_576 },
	{ id: "xiaomi/mimo-v2.5-pro", contextWindowTokens: 1_050_000 },
];

export const SUPPORTED_MODEL_IDS: ReadonlySet<string> = new Set(
	SUPPORTED_MODELS.map((m) => m.id),
);
type AgentOverrides = Infer<typeof agentOverridesValidator>;

/**
 * Read-time guard for persisted `agentOverrides`: drops `model` pins that
 * left the catalog (turn falls back to the agent default) while keeping
 * provider/promptOverride. Replaces data migrations for model retirement.
 */
export function sanitizeAgentOverrideModels(
	overrides: AgentOverrides | undefined,
): AgentOverrides | undefined {
	if (!overrides) return undefined;
	let changed = false;
	const next: AgentOverrides = {};
	for (const [agentName, override] of Object.entries(overrides)) {
		if (
			override.model !== undefined &&
			!SUPPORTED_MODEL_IDS.has(override.model)
		) {
			changed = true;
			const { model: _retired, ...rest } = override;
			next[agentName] = rest;
			continue;
		}
		next[agentName] = override;
	}
	return changed ? next : overrides;
}
